import React from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { containerTransientProps } from './MapPin';
import type { MapPinProps } from './MapPin';
import type { MapPinColor } from './MapPin.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { OverlayView } from '@react-google-maps/api';
import { styles } from './MapPin.styles';
import {
  StyledPinContainer as StyledMapPinLabelContainer,
  StyledPinBubble as StyledMapPinLabel,
  StyledPinTip as StyledMapPinLabelTip,
  StyledPinTipShadow as StyledMapPinLabelTipShadow,
} from '@shared/components/MapPinVisuals';

interface VenueMapPinProps extends Readonly<Pick<MapPinProps, 'position'>> {
  readonly label?: string;
  /** Show the 🎉 prefix. Defaults to true for the venue pin; pass false for the search-location pin, where it'd misleadingly read as a celebration of the searched place. */
  readonly showEmoji?: boolean;
  /** Pin color override (e.g. the cash-fund gold) — matches the hotel markers' markerColor. Defaults to the blurple brand color. */
  readonly highlightColor?: MapPinColor;
}

/**
 * VenueMapPin - Map pin for the venue or search location.
 *
 * Renders a blurple pin so it pops against the white hotel price pins on the
 * map. The venue pin additionally shows a 🎉 prefix. Can be used for venue
 * location or search location.
 */
export const VenueMapPin: React.FC<VenueMapPinProps> = ({ position, label, showEmoji = true, highlightColor = 'newUIBrandPrimary' }) => {
  const { tq } = useTranslation('travelMap');

  return (
    <OverlayView
      mapPaneName={OverlayView.FLOAT_PANE}
      position={position}
    >
      <StyledMapPinLabelContainer
        role="group"
        __css={styles.joyMapPinLabelContainer({ isActive: false, isClickable: false, showOnTop: true, isCardVisible: false })}
        {...containerTransientProps({ isActive: false, isClickable: false, isCardVisible: false, showOnTop: true })}
      >
        <StyledMapPinLabelTipShadow
          __css={styles.joyMapPinLabelTipShadow}
          filter="none"
        />
        <StyledMapPinLabel
          __css={styles.joyMapPinLabel({ isActive: false, isClickable: false, isCardVisible: false })}
          boxShadow="0 1px 3px -1px rgba(0, 0, 0, 0.12)"
          $isHighlighted
          $mapPinHighlightColor={highlightColor}
        >
          <Flex
            alignItems="center"
            columnGap={1}
          >
            {showEmoji && (
              <TextV2
                typographyVariant="label3"
                fontSize={pxToRem(13)}
                lineHeight={1}
                aria-hidden="true"
              >
                🎉
              </TextV2>
            )}
            <TextV2
              typographyVariant="label3"
              fontSize={pxToRem(13)}
              color="white"
              fontWeight={600}
              paddingX={2}
              maxWidth={pxToRem(170)}
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
              fontFamily="Inter UI"
            >
              {label || tq`Event Venue`}
            </TextV2>
          </Flex>
        </StyledMapPinLabel>
        <StyledMapPinLabelTip
          __css={styles.joyMapPinLabelTip({ isActive: false, isClickable: false, isCardVisible: false })}
          $isHighlighted
          $mapPinHighlightColor={highlightColor}
        />
      </StyledMapPinLabelContainer>
    </OverlayView>
  );
};
